import { getAssetsPath } from "./pathConfig";
import { Settings } from "../settings";
import { FEATURES_CONFIG } from "../featuresConfig";

// Use centralized path configuration
const BASE_PATH = getAssetsPath();
const isSecondaryTheme = Settings.APP.UI_THEME === "secondary";

// Theme-specific font family string
const THEME_FONT_FAMILY = isSecondaryTheme
  ? '"Oxanium", -apple-system, blinkmacsystemfont, "Segoe UI", "Oxygen", "Cantarell", "Roboto", "Fira Sans", "Droid Sans", "Helvetica", "Helvetica Neue", "Arial", sans-serif'
  : '"Noto Sans", -apple-system, blinkmacsystemfont, "Segoe UI", "Oxygen", "Cantarell", "Roboto", "Fira Sans", "Droid Sans", "Helvetica", "Helvetica Neue", "Arial", sans-serif';

/**
 * Generate @font-face declarations based on active theme
 */
const getThemeFontFaces = () => {
  if (isSecondaryTheme) {
    return `
    @font-face {
      font-display: swap;
      font-family: "Oxanium";
      font-style: normal;
      font-weight: 400;
      src: url("${BASE_PATH}/fonts/oxanium/oxanium-v21-latin_latin-ext-regular.woff2") format("woff2");
    }

    @font-face {
      font-display: swap;
      font-family: "Oxanium";
      font-style: normal;
      font-weight: 500;
      src: url("${BASE_PATH}/fonts/oxanium/oxanium-v21-latin_latin-ext-500.woff2") format("woff2");
    }

    @font-face {
      font-display: swap;
      font-family: "Oxanium";
      font-style: normal;
      font-weight: 600;
      src: url("${BASE_PATH}/fonts/oxanium/oxanium-v21-latin_latin-ext-600.woff2") format("woff2");
    }

    @font-face {
      font-display: swap;
      font-family: "Oxanium";
      font-style: normal;
      font-weight: 700;
      src: url("${BASE_PATH}/fonts/oxanium/oxanium-v21-latin_latin-ext-700.woff2") format("woff2");
    }

    @font-face {
      font-display: swap;
      font-family: "Oxanium";
      font-style: normal;
      font-weight: 800;
      src: url("${BASE_PATH}/fonts/oxanium/oxanium-v21-latin_latin-ext-800.woff2") format("woff2");
    }`;
  }

  // Primary theme: Noto Sans
  return `
    @font-face {
      font-display: swap;
      font-family: "Noto Sans";
      font-style: normal;
      font-weight: 300;
      src: url("${BASE_PATH}/fonts/noto-sans/noto-sans-v42-latin-300.woff2") format("woff2");
    }

    @font-face {
      font-display: swap;
      font-family: "Noto Sans";
      font-style: normal;
      font-weight: 400;
      src: url("${BASE_PATH}/fonts/noto-sans/noto-sans-v42-latin-regular.woff2") format("woff2");
    }

    @font-face {
      font-display: swap;
      font-family: "Noto Sans";
      font-style: normal;
      font-weight: 500;
      src: url("${BASE_PATH}/fonts/noto-sans/noto-sans-v42-latin-500.woff2") format("woff2");
    }

    @font-face {
      font-display: swap;
      font-family: "Noto Sans";
      font-style: normal;
      font-weight: 600;
      src: url("${BASE_PATH}/fonts/noto-sans/noto-sans-v42-latin-600.woff2") format("woff2");
    }

    @font-face {
      font-display: swap;
      font-family: "Noto Sans";
      font-style: normal;
      font-weight: 700;
      src: url("${BASE_PATH}/fonts/noto-sans/noto-sans-v42-latin-700.woff2") format("woff2");
    }

    @font-face {
      font-display: swap;
      font-family: "Noto Sans";
      font-style: normal;
      font-weight: 800;
      src: url("${BASE_PATH}/fonts/noto-sans/noto-sans-v42-latin-800.woff2") format("woff2");
    }

    @font-face {
      font-display: swap;
      font-family: "Noto Sans";
      font-style: normal;
      font-weight: 900;
      src: url("${BASE_PATH}/fonts/noto-sans/noto-sans-v42-latin-900.woff2") format("woff2");
    }`;
};

/**
 * Injects CSS styles dynamically into the document
 * This allows bundle.js to be self-contained - fonts and styles work wherever it's injected
 */
export const injectStyles = () => {
  // Check if styles are already injected (prevents duplicate injection)
  if (document.getElementById("injected-styles")) {
    return;
  }

  const styleElement = document.createElement("style");
  styleElement.id = "injected-styles";

  // Build CSS string with all styles
  const css = `
    /* ==========================================================================
       FONT DECLARATIONS
       ========================================================================== */

    ${FEATURES_CONFIG.HOLD_AND_WIN.ENABLED ? `
    @font-face {
      font-display: swap;
      font-family: "Troika";
      font-style: normal;
      font-weight: normal;
      src: url("${BASE_PATH}/fonts/troika/troika.otf") format("opentype");
    }` : ''}

    ${getThemeFontFaces()}

    /* ==========================================================================
       CSS RESET & BASE STYLES
       ========================================================================== */

    * {
      box-sizing: border-box;
      margin: 0;
      padding: 0;
    }

    html,
    body {
      width: 100%;
      height: 100%;
      margin: 0;
      padding: 0;
      overflow: hidden;
      background-color: #000000;
      --game-height: 100vh;
    }

    body {
      font-family: ${THEME_FONT_FAMILY};
      -webkit-user-select: none;
      -moz-user-select: none;
      -ms-user-select: none;
      user-select: none;
      -webkit-touch-callout: none;
      -webkit-tap-highlight-color: transparent;
      touch-action: none;
    }

    /* Game container - clips the canvas to viewport */
    #game {
      width: 100%;
      height: 100%;
      overflow: hidden;
      position: relative;
      font-family: ${THEME_FONT_FAMILY};
    }

    /* Canvas fills viewport but PixiJS renders full stage content */
    /* Canvas should maintain aspect ratio and be centered */
    canvas {
      display: block;
      max-width: 100%;
      max-height: 100%;
      width: auto !important;
      height: auto !important;
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
    }
  `;

  styleElement.textContent = css;
  document.head.appendChild(styleElement);
};

/**
 * Preload custom fonts to ensure they're available for PIXI
 */
const preloadFonts = async () => {
  if (!document.fonts || !window.FontFace) {
    return; // Browser doesn't support Font Loading API
  }

  try {
    // Preload Troika font only if Hold and Win is enabled
    if (FEATURES_CONFIG.HOLD_AND_WIN.ENABLED) {
      const troikaFont = new FontFace(
        'Troika',
        `url(${BASE_PATH}/fonts/troika/troika.otf) format("opentype")`
      );

      await troikaFont.load();
      document.fonts.add(troikaFont);
    }

  } catch (error) {
    // Font preload failed - fallback fonts will be used
  }

  try {
    if (isSecondaryTheme) {
      // Preload Oxanium fonts
      const oxaniumRegular = new FontFace(
        'Oxanium',
        `url(${BASE_PATH}/fonts/oxanium/oxanium-v21-latin_latin-ext-regular.woff2) format("woff2")`,
        { weight: '400' }
      );

      const oxaniumBold = new FontFace(
        'Oxanium',
        `url(${BASE_PATH}/fonts/oxanium/oxanium-v21-latin_latin-ext-700.woff2) format("woff2")`,
        { weight: '700' }
      );

      await Promise.all([
        oxaniumRegular.load(),
        oxaniumBold.load()
      ]);

      document.fonts.add(oxaniumRegular);
      document.fonts.add(oxaniumBold);
    } else {
      // Preload Noto Sans fonts (most common weights)
      const notoSansRegular = new FontFace(
        'Noto Sans',
        `url(${BASE_PATH}/fonts/noto-sans/noto-sans-v42-latin-regular.woff2) format("woff2")`,
        { weight: '400' }
      );

      const notoSansBold = new FontFace(
        'Noto Sans',
        `url(${BASE_PATH}/fonts/noto-sans/noto-sans-v42-latin-700.woff2) format("woff2")`,
        { weight: '700' }
      );

      const notoSansExtraBold = new FontFace(
        'Noto Sans',
        `url(${BASE_PATH}/fonts/noto-sans/noto-sans-v42-latin-800.woff2) format("woff2")`,
        { weight: '800' }
      );

      await Promise.all([
        notoSansRegular.load(),
        notoSansBold.load(),
        notoSansExtraBold.load()
      ]);

      document.fonts.add(notoSansRegular);
      document.fonts.add(notoSansBold);
      document.fonts.add(notoSansExtraBold);
    }

  } catch (error) {
    // Font preload failed - fallback fonts will be used
  }
};

/**
 * Initializes all styles - call this once at app startup
 * This makes bundle.js completely self-contained
 */
export const initializeStyles = async () => {
  injectStyles();
  await preloadFonts();
};
